import React, { useEffect, useState } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

import FormField from '@ingka/form-field';
import InlineMessage from '@ingka/inline-message';
import InputField from '@ingka/input-field';
import ListView, { ListViewItem } from '@ingka/list-view';
import Modal, { ModalBody, ModalHeader, Sheets } from '@ingka/modal';
import Tag from '@ingka/tag';

import { commandStore } from '../command-store';
import { useCommands } from '../hooks/use-commands';
import { getSharedHotkeyManager } from '../hotkey-singletons';
import { getRegisteredCommand, isCommandEnabled } from '../register-commands';
import type { Command } from '../types';

import styles from '../styles.module.scss';

const subscribeToOpen = (onStoreChange: () => void): (() => void) => {
    const subscription = commandStore.subscribe(onStoreChange);
    return () => {
        subscription.unsubscribe();
    };
};

const getIsOpenSnapshot = (): boolean => commandStore.state.isOpen;

interface CommandMenuProps {
    title: string;
    searchPlaceholder: string;
    ariaCloseText: string;
    noCommandsMessage?: string;
    noMatchingCommandsMessage?: string;
    prefix?: string;
}

export const CommandMenu = ({
    title,
    searchPlaceholder,
    ariaCloseText,
    noCommandsMessage,
    noMatchingCommandsMessage,
    prefix,
}: CommandMenuProps) => {
    const isOpen = useSyncExternalStore(subscribeToOpen, getIsOpenSnapshot);
    const [inputValue, setInputValue] = useState('');
    const commands = useCommands();
    const prefixProp = prefix ? { prefix } : {};

    useEffect(() => {
        const handle = getSharedHotkeyManager().register(
            'Mod+K',
            () => {
                commandStore.setState((previous) => ({ isOpen: !previous.isOpen, commands: previous.commands }));
            },
            { preventDefault: true }
        );
        return () => {
            handle.unregister();
        };
    }, []);

    const close = () => {
        commandStore.setState((previous) => ({ isOpen: false, commands: previous.commands }));
        setInputValue('');
    };

    const visibleCommands = commands.filter(
        (command): command is Extract<Command, { hidden?: false }> => !command.hidden
    );
    const filteredCommands = visibleCommands.filter((command) =>
        command.description.toLowerCase().includes(inputValue.toLowerCase())
    );
    const emptyStateMessage =
        visibleCommands.length === 0
            ? noCommandsMessage
            : filteredCommands.length === 0
              ? noMatchingCommandsMessage
              : undefined;

    const groupedCommands = filteredCommands.reduce<Map<string, Command[]>>((groups, command) => {
        const key = command.group ?? '';
        const existing = groups.get(key);
        if (existing) {
            existing.push(command);
        } else {
            groups.set(key, [command]);
        }
        return groups;
    }, new Map());

    const handleSelect = (commandId: string) => {
        const command = getRegisteredCommand(commandId);
        if (!command || !isCommandEnabled(commandId)) {
            return;
        }
        close();
        command.onSelect('palette');
    };

    const renderCommandList = (items: Command[], id: string) => (
        <ListView {...prefixProp} id={id} size="small">
            {items.map((command) => (
                <ListViewItem
                    {...prefixProp}
                    key={command.id}
                    title={
                        <div className={styles.shortcutRow}>
                            <span>{command.description}</span>
                            {command.keys.length > 0 && (
                                <div className={styles.shortcutKeys}>
                                    {command.keys.map((key) => (
                                        <Tag {...prefixProp} key={key} label={key.toLowerCase()} subtle />
                                    ))}
                                </div>
                            )}
                        </div>
                    }
                    control="navigational"
                    controlIcon={null}
                    disabled={!isCommandEnabled(command.id)}
                    onClick={() => {
                        handleSelect(command.id);
                    }}
                />
            ))}
        </ListView>
    );

    return (
        <Modal {...prefixProp} visible={isOpen} handleCloseBtn={close}>
            <Sheets
                {...prefixProp}
                labelledById="command-menu-title"
                header={
                    <ModalHeader
                        {...prefixProp}
                        title={title}
                        titleId="command-menu-title"
                        ariaCloseTxt={ariaCloseText}
                    />
                }
                footer={null}
            >
                <ModalBody {...prefixProp}>
                    <FormField {...prefixProp}>
                        <InputField
                            {...prefixProp}
                            id="command-menu-search"
                            type="text"
                            label={searchPlaceholder}
                            value={inputValue}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setInputValue(e.target.value);
                            }}
                        />
                    </FormField>
                    {emptyStateMessage ? (
                        <InlineMessage {...prefixProp} body={emptyStateMessage} variant="informative" />
                    ) : (
                        [...groupedCommands.entries()].map(([group, items]) => (
                            <div key={group}>
                                {group && <p className={styles.groupLabel}>{group}</p>}
                                {renderCommandList(items, `command-menu-list-${group || 'ungrouped'}`)}
                            </div>
                        ))
                    )}
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
